import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { schemaAPI } from '../services/api';
import PaginationControls from '../components/PaginationControls';

import DataSource from '../components/DataSource';
// Utility function to safely display values, handling null/undefined
const safeDisplay = (value, defaultValue = '-') => {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  return String(value);
};

function Schema() {
  const [tabValue, setTabValue] = useState(0);
  const [attributes, setAttributes] = useState([]);
  // Which DC actually served the schema.
  const [dataSource, setDataSource] = useState(null);
  const [objectClasses, setObjectClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('attribute');

  // Search states
  const [attributeSearch, setAttributeSearch] = useState('');
  const [objectClassSearch, setObjectClassSearch] = useState('');

  // Pagination state for attributes
  const [attrPage, setAttrPage] = useState(1);
  const [attrPageSize, setAttrPageSize] = useState(50);
  const [attrTotalCount, setAttrTotalCount] = useState(0);
  const [attrTotalPages, setAttrTotalPages] = useState(1);

  // Pagination state for object classes
  const [objPage, setObjPage] = useState(1);
  const [objPageSize, setObjPageSize] = useState(50);
  const [objTotalCount, setObjTotalCount] = useState(0);
  const [objTotalPages, setObjTotalPages] = useState(1);

  // Attribute form
  const [attributeForm, setAttributeForm] = useState({
    attribute_name: '',
    attribute_type: 'String',
    syntax: '2.5.5.12',
    attribute_id: '',
    is_single_valued: true,
  });

  // Attribute type options with their corresponding syntax OIDs
  const attributeTypes = [
    { value: 'String', label: 'String (Unicode)', syntax: '2.5.5.12' },
    { value: 'Integer', label: 'Integer', syntax: '2.5.5.9' },
    { value: 'Boolean', label: 'Boolean', syntax: '2.5.5.8' },
    { value: 'DN', label: 'Distinguished Name', syntax: '2.5.5.1' },
    { value: 'Binary', label: 'Binary (Octet String)', syntax: '2.5.5.10' },
  ];

  // Object class form
  const [objectClassForm, setObjectClassForm] = useState({
    class_name: '',
    add_attributes: '',
  });

  useEffect(() => {
    loadSchemaData();

    // Listen for sudo session established event
    const handleSudoSession = () => {
      loadSchemaData();
    };

    window.addEventListener('sudo-session-established', handleSudoSession);

    return () => {
      window.removeEventListener('sudo-session-established', handleSudoSession);
    };
  }, []);

  // Reload data when search changes (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadSchemaData(1, attrPageSize, 1, objPageSize);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [attributeSearch, objectClassSearch]);

  const loadSchemaData = async (attrPageNum = attrPage, attrSize = attrPageSize, objPageNum = objPage, objSize = objPageSize) => {
    setLoading(true);
    setError('');
    try {
      const [attrRes, classRes] = await Promise.all([
        schemaAPI.getAttributes({
          page: attrPageNum,
          page_size: attrSize,
          search: attributeSearch || undefined
        }),
        schemaAPI.getObjectClasses({
          page: objPageNum,
          page_size: objSize,
          search: objectClassSearch || undefined
        }),
      ]);

      // Set attributes with pagination info
      setAttributes(attrRes.data.results || []);
      setAttrTotalCount(attrRes.data.count || 0);
      setDataSource(attrRes.data.source || null);
      setAttrTotalPages(attrRes.data.total_pages || 1);
      setAttrPage(attrRes.data.current_page || 1);
      setAttrPageSize(attrRes.data.page_size || 50);

      // Set object classes with pagination info
      setObjectClasses(classRes.data.results || []);
      setObjTotalCount(classRes.data.count || 0);
      setObjTotalPages(classRes.data.total_pages || 1);
      setObjPage(classRes.data.current_page || 1);
      setObjPageSize(classRes.data.page_size || 50);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load schema');
    } finally {
      setLoading(false);
    }
  };

  const handleAttrPageChange = (event, newPage) => {
    loadSchemaData(newPage, attrPageSize, objPage, objPageSize);
  };

  const handleAttrPageSizeChange = (event) => {
    const newSize = event.target.value;
    loadSchemaData(1, newSize, objPage, objPageSize);
  };

  const handleObjPageChange = (event, newPage) => {
    loadSchemaData(attrPage, attrPageSize, newPage, objPageSize);
  };

  const handleObjPageSizeChange = (event) => {
    const newSize = event.target.value;
    loadSchemaData(attrPage, attrPageSize, 1, newSize);
  };

  const handleOpenDialog = (mode) => {
    setDialogMode(mode);
    if (mode === 'attribute') {
      setAttributeForm({
        attribute_name: '',
        attribute_type: 'String',
        syntax: '2.5.5.12',
        attribute_id: '',
        is_single_valued: true,
      });
    } else {
      setObjectClassForm({ class_name: '', add_attributes: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSubmit = async () => {
    try {
      if (dialogMode === 'attribute') {
        await schemaAPI.addAttribute(attributeForm);
      } else {
        const attributes = objectClassForm.add_attributes
          .split(',')
          .map((a) => a.trim())
          .filter((a) => a);
        await schemaAPI.modifyObjectClass({
          class_name: objectClassForm.class_name,
          add_attributes: attributes,
        });
      }
      handleCloseDialog();
      loadSchemaData();
    } catch (err) {
      setError(err.response?.data?.error || 'Operation failed');
    }
  };

  // Note: Search is now handled server-side in backend
  // No need for client-side filtering

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3,
                 flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4">Schema Management</Typography>
          <DataSource source={dataSource} sx={{ mt: 0.5 }} />
        </Box>
        <Box>
          <IconButton onClick={loadSchemaData} sx={{ mr: 1 }}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Alert severity="warning" sx={{ mb: 3 }}>
        Modifying schema can have serious consequences. Proceed with caution.
      </Alert>

      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            <Typography variant="h6">Guide: Creating New Schema Attributes</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              What are Schema Attributes?
            </Typography>
            <Typography variant="body2" paragraph>
              Schema attributes are custom fields you can add to Active Directory objects (users, groups, OUs, etc.).
              They allow you to store additional information beyond the standard AD attributes.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
              Step-by-Step Process:
            </Typography>
            <Typography variant="body2" component="div">
              <ol>
                <li>
                  <strong>Create the Attribute</strong>
                  <ul>
                    <li>Click "Add Attribute" in the Attributes tab</li>
                    <li>Choose a descriptive name (e.g., "employeeRole", "departmentCode")</li>
                    <li>Select the appropriate data type (String, Integer, Boolean, etc.)</li>
                    <li>Optionally specify a custom OID (or leave blank to auto-generate)</li>
                  </ul>
                </li>
                <li style={{ marginTop: '12px' }}>
                  <strong>Add to Object Class</strong>
                  <ul>
                    <li>Switch to the "Object Classes" tab</li>
                    <li>Click "Modify Object Class"</li>
                    <li>For user attributes, use "user" as the class name</li>
                    <li>For group attributes, use "group"</li>
                    <li>Enter your attribute name(s) in the comma-separated list</li>
                  </ul>
                </li>
                <li style={{ marginTop: '12px' }}>
                  <strong>Verify and Use</strong>
                  <ul>
                    <li>The attribute will now be available on objects of that class</li>
                    <li>Use LDAP tools or custom applications to set/read values</li>
                    <li>Changes are permanent and cannot be undone</li>
                  </ul>
                </li>
              </ol>
            </Typography>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Best Practices:</strong>
                <br />
                • Test schema changes in a development environment first
                <br />
                • Use descriptive names in camelCase (e.g., "customField" not "cf")
                <br />
                • Document your custom attributes and their purpose
                <br />
                • Consider whether you need a single-valued or multi-valued attribute
                <br />• Schema extensions cannot be removed, only disabled
              </Typography>
            </Alert>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Important Notes:</strong>
                <br />
                • Schema changes replicate across all domain controllers
                <br />
                • Changes are permanent and cannot be undone
                <br />
                • Invalid schema modifications can break AD functionality
                <br />• Always back up your domain before making schema changes
              </Typography>
            </Alert>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label={`Attributes (${attributes.length})`} />
          <Tab label={`Object Classes (${objectClasses.length})`} />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2 }}>
              <TextField
                placeholder="Search attributes..."
                value={attributeSearch}
                onChange={(e) => setAttributeSearch(e.target.value)}
                size="small"
                sx={{ flexGrow: 1, maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: attributeSearch && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setAttributeSearch('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('attribute')}
              >
                Add Attribute
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : attributes.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  {attributeSearch ? 'No attributes found matching your search.' : 'No attributes available.'}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>OID</TableCell>
                      <TableCell>Syntax</TableCell>
                      <TableCell>Single Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attributes.map((attr, index) => (
                      <TableRow key={index} hover>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {safeDisplay(attr.lDAPDisplayName)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {safeDisplay(attr.attributeID)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {safeDisplay(attr.attributeSyntax)}
                        </TableCell>
                        <TableCell>
                          {attr.isSingleValued !== null && attr.isSingleValued !== undefined ? (
                            attr.isSingleValued ? (
                              <Chip label="Yes" size="small" color="primary" />
                            ) : (
                              <Chip label="No" size="small" />
                            )
                          ) : (
                            <Chip label="Unknown" size="small" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {attributeSearch && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  Found {attrTotalCount} attribute{attrTotalCount !== 1 ? 's' : ''} matching "{attributeSearch}"
                </Typography>
              </Box>
            )}

            <PaginationControls
              count={attrTotalCount}
              page={attrPage}
              pageSize={attrPageSize}
              totalPages={attrTotalPages}
              onPageChange={handleAttrPageChange}
              onPageSizeChange={handleAttrPageSizeChange}
            />
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2 }}>
              <TextField
                placeholder="Search object classes..."
                value={objectClassSearch}
                onChange={(e) => setObjectClassSearch(e.target.value)}
                size="small"
                sx={{ flexGrow: 1, maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: objectClassSearch && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setObjectClassSearch('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('objectclass')}
              >
                Modify Object Class
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : objectClasses.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  {objectClassSearch ? 'No object classes found matching your search.' : 'No object classes available.'}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Class Name</TableCell>
                      <TableCell>OID</TableCell>
                      <TableCell>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {objectClasses.map((objClass, index) => (
                      <TableRow key={index} hover>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {safeDisplay(objClass.lDAPDisplayName)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {safeDisplay(objClass.governsID)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={safeDisplay(objClass.objectClassCategory, 'Structural')}
                            size="small"
                            color={objClass.type ? 'default' : 'primary'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {objectClassSearch && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  Found {objTotalCount} object class{objTotalCount !== 1 ? 'es' : ''} matching "{objectClassSearch}"
                </Typography>
              </Box>
            )}

            <PaginationControls
              count={objTotalCount}
              page={objPage}
              pageSize={objPageSize}
              totalPages={objTotalPages}
              onPageChange={handleObjPageChange}
              onPageSizeChange={handleObjPageSizeChange}
            />
          </Box>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'attribute' ? 'Add Schema Attribute' : 'Modify Object Class'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {dialogMode === 'attribute' ? (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Schema changes are permanent and cannot be undone. Test in a development environment first.
                </Alert>
                <TextField
                  fullWidth
                  label="Attribute Name"
                  value={attributeForm.attribute_name}
                  onChange={(e) =>
                    setAttributeForm({ ...attributeForm, attribute_name: e.target.value })
                  }
                  margin="normal"
                  required
                  helperText="CamelCase recommended (e.g., employeeRole, departmentCode)"
                />
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Attribute Type</InputLabel>
                  <Select
                    value={attributeForm.attribute_type}
                    label="Attribute Type"
                    onChange={(e) => {
                      const selectedType = attributeTypes.find(t => t.value === e.target.value);
                      setAttributeForm({
                        ...attributeForm,
                        attribute_type: e.target.value,
                        syntax: selectedType?.syntax || '2.5.5.12'
                      });
                    }}
                  >
                    {attributeTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Data type for the attribute</FormHelperText>
                </FormControl>
                <TextField
                  fullWidth
                  label="LDAP Syntax OID"
                  value={attributeForm.syntax}
                  onChange={(e) =>
                    setAttributeForm({ ...attributeForm, syntax: e.target.value })
                  }
                  margin="normal"
                  required
                  helperText="Auto-filled based on type selection"
                  disabled
                />
                <TextField
                  fullWidth
                  label="Attribute ID (OID)"
                  value={attributeForm.attribute_id}
                  onChange={(e) =>
                    setAttributeForm({ ...attributeForm, attribute_id: e.target.value })
                  }
                  margin="normal"
                  helperText="Optional: Leave blank to auto-generate. Format: 1.3.6.1.4.1.7165.777.0.X"
                />
              </>
            ) : (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Schema changes are permanent and cannot be undone. Test in a development environment first.
                </Alert>
                <Alert severity="info" sx={{ mb: 2 }}>
                  To add custom attributes to users, use "user" as the object class name.
                </Alert>
                <TextField
                  fullWidth
                  label="Object Class Name"
                  value={objectClassForm.class_name}
                  onChange={(e) =>
                    setObjectClassForm({
                      ...objectClassForm,
                      class_name: e.target.value,
                    })
                  }
                  margin="normal"
                  required
                  helperText="Common: user, group, organizationalUnit, computer"
                />
                <TextField
                  fullWidth
                  label="Attributes to Add"
                  value={objectClassForm.add_attributes}
                  onChange={(e) =>
                    setObjectClassForm({
                      ...objectClassForm,
                      add_attributes: e.target.value,
                    })
                  }
                  margin="normal"
                  required
                  helperText="Comma-separated list of attribute names (must be created first)"
                  multiline
                  rows={3}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Schema;
